import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import LegalLayout from "./LegalLayout";
import { LEGAL_LAST_UPDATED } from "../../data/legal";

const EASE = [0.22, 1, 0.36, 1];

const bodyStyle = {
    color: "var(--muted-foreground)",
    fontSize: "1rem",
    lineHeight: 1.85,
};

function Block({ block }) {
    if (typeof block === "string") {
        return (
            <p className="font-sans" style={bodyStyle}>
                {block}
            </p>
        );
    }
    if (block.list) {
        return (
            <ul className="flex flex-col gap-3">
                {block.list.map((item, i) => (
                    <li key={i} className="flex gap-4">
                        <span
                            aria-hidden="true"
                            style={{
                                color: "var(--accent)",
                                lineHeight: 1.85,
                                flexShrink: 0,
                            }}
                        >
                            —
                        </span>
                        <span className="font-sans" style={bodyStyle}>
                            {item}
                        </span>
                    </li>
                ))}
            </ul>
        );
    }
    if (block.link) {
        const { before, label, to, after } = block.link;
        return (
            <p className="font-sans" style={bodyStyle}>
                {before}
                <Link
                    to={to}
                    className="link-underline"
                    style={{ color: "var(--ink)", fontWeight: 500 }}
                >
                    {label}
                </Link>
                {after}
            </p>
        );
    }
    return null;
}

export default function LegalPage({ doc }) {
    return (
        <LegalLayout>
            <article
                data-testid={`legal-page-${doc.slug}`}
                className="mx-auto max-w-[820px] px-6 pb-28 pt-12 md:px-8 md:pb-40 md:pt-20"
            >
                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: EASE }}
                >
                    <span className="eyebrow">{doc.eyebrow}</span>
                    <h1
                        className="mt-6 font-serif"
                        style={{
                            fontWeight: 300,
                            lineHeight: 0.98,
                            fontSize: "clamp(2.6rem, 6vw, 4.75rem)",
                        }}
                    >
                        {doc.title}
                    </h1>
                    <p
                        className="mt-6 font-sans"
                        style={{
                            color: "var(--muted-foreground)",
                            fontSize: "0.9rem",
                            letterSpacing: "0.04em",
                        }}
                    >
                        Ultima actualizare:{" "}
                        <span style={{ color: "var(--ink)" }}>
                            {LEGAL_LAST_UPDATED}
                        </span>
                    </p>
                </motion.div>

                {/* Thin golden line */}
                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
                    className="mt-10 h-px w-full"
                    style={{
                        backgroundColor: "var(--accent)",
                        opacity: 0.7,
                        transformOrigin: "left",
                    }}
                />

                {/* Sections */}
                <div className="mt-16 flex flex-col gap-14 md:gap-16">
                    {doc.sections.map((s, i) => (
                        <motion.section
                            key={s.n}
                            data-testid={`legal-section-${s.n}`}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-8% 0px -8% 0px" }}
                            transition={{ duration: 0.9, ease: EASE }}
                        >
                            <h2
                                className="font-serif"
                                style={{
                                    fontWeight: 300,
                                    lineHeight: 1.1,
                                    fontSize: "clamp(1.6rem, 3vw, 2.35rem)",
                                }}
                            >
                                <span style={{ color: "var(--accent)" }}>
                                    {s.n}.
                                </span>{" "}
                                {s.title}
                            </h2>
                            <div className="mt-6 flex flex-col gap-4">
                                {s.body.map((block, bi) => (
                                    <Block key={bi} block={block} />
                                ))}
                            </div>
                        </motion.section>
                    ))}
                </div>
            </article>
        </LegalLayout>
    );
}
